/**
 * @fileoverview Wallet Layout - Standalone experience for certificate owners
 * No sidebar, clean navigation focused on certificate viewing
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/contexts/LanguageContext';
import {
    LogOut,
    User,
    Settings,
    ChevronDown,
    Wallet,
    Globe
} from 'lucide-react';

export const WalletLayout = ({ children }) => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const { currentLanguage, setLanguage, languages } = useLanguage();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [isLangOpen, setIsLangOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="flex flex-col h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
            {/* Top Navigation */}
            <nav className="bg-white border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo */}
                        <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/wallet')}>
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                                <Wallet className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">
                                    {t('wallet.title', 'My Certificates')}
                                </h1>
                                <p className="text-xs text-slate-500">
                                    {t('wallet.subtitle', 'Secure Document Wallet')}
                                </p>
                            </div>
                        </div>

                        {/* Right side: Language Selector + User Menu */}
                        <div className="flex items-center gap-3">
                            {/* Language Selector */}
                            <div className="relative">
                                <button
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all text-sm focus:outline-none"
                                    onClick={() => { setIsLangOpen(!isLangOpen); setShowUserMenu(false); }}
                                    onBlur={() => setTimeout(() => setIsLangOpen(false), 200)}
                                    title={t('nav.change_language', 'Cambiar idioma')}
                                >
                                    <Globe className="w-4 h-4 text-blue-500" />
                                    <span className="uppercase font-semibold text-xs">{currentLanguage}</span>
                                    <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isLangOpen && (
                                    <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 z-50">
                                        <div className="px-3 py-1.5 border-b border-slate-50">
                                            <p className="text-xs font-bold text-slate-400 uppercase">{t('nav.language', 'Idioma')}</p>
                                        </div>
                                        {languages.map((lang) => (
                                            <button
                                                key={lang.code}
                                                onClick={() => {
                                                    setLanguage(lang.code);
                                                    setIsLangOpen(false);
                                                }}
                                                className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors text-sm flex items-center justify-between gap-2 ${currentLanguage === lang.code
                                                    ? 'text-blue-600 font-bold bg-blue-50/50'
                                                    : 'text-slate-600'
                                                    }`}
                                            >
                                                <span className="flex items-center gap-2">
                                                    <span className="text-base">{lang.flag || '🏳️'}</span>
                                                    <span>{lang.native_name || lang.name}</span>
                                                </span>
                                                {currentLanguage === lang.code && (
                                                    <ChevronDown className="w-3 h-3 text-blue-500 rotate-90" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* User Menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors"
                                >
                                    <div className="w-8 h-8 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="text-left hidden sm:block">
                                        <p className="text-sm font-medium text-slate-900">
                                            {user?.first_name || user?.name || user?.email}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {user?.roles?.find(r => r.role_name === 'student')?.role_display_name || 'Certificate Owner'}
                                        </p>
                                    </div>
                                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {showUserMenu && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setShowUserMenu(false)}
                                        />
                                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-20">
                                            <button
                                                onClick={() => {
                                                    navigate('/settings');
                                                    setShowUserMenu(false);
                                                }}
                                                className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-slate-50 transition-colors text-left"
                                            >
                                                <Settings className="w-4 h-4 text-slate-600" />
                                                <span className="text-sm text-slate-700">{t('common.settings', 'Settings')}</span>
                                            </button>
                                            <hr className="my-2 border-slate-200" />
                                            <button
                                                onClick={handleLogout}
                                                className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-red-50 transition-colors text-left"
                                            >
                                                <LogOut className="w-4 h-4 text-red-600" />
                                                <span className="text-sm text-red-700">{t('auth.logout', 'Logout')}</span>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {children}
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-white border-t border-slate-200 py-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-center text-sm text-slate-500">
                        {t('wallet.footer', 'Powered by AeroCert - Blockchain-verified certificates')}
                    </p>
                </div>
            </footer>
        </div>
    );
};
